import { X, HeartPulse, TrendingUp, TrendingDown, Sparkles } from 'lucide-react'
import { AISparkle } from '@/components/ui/AISparkle'
import { cn } from '@/lib/utils'
import { PATIENT, LABS, TRENDS, PATTERNS, CONNECTED_SOURCES } from '../data/record'

// Compact, polished health snapshot shown in the chat canvas (the artifact icon).
export function CanvasPanel({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex h-full flex-col bg-surface-canvas">
      <header className="flex items-center gap-2 h-16 px-4 border-b border-border shrink-0">
        <HeartPulse className="size-5 text-foreground" />
        <div className="flex-1 leading-tight">
          <div className="text-sm font-semibold text-foreground">Health snapshot</div>
          <div className="text-[11px] text-muted-foreground">{PATIENT.name} · {CONNECTED_SOURCES.length} sources</div>
        </div>
        <button onClick={onClose} className="p-2 -mr-2 rounded-md hover:bg-muted" aria-label="Close">
          <X className="size-5 text-foreground" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Vital tiles */}
        <div className="grid grid-cols-2 gap-2.5">
          {TRENDS.map((t) => (
            <div key={t.metric} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground truncate">{t.metric}</span>
                {t.dir === 'up'
                  ? <TrendingUp className={cn('size-3', t.concern ? 'text-alert-text' : 'text-muted-foreground')} />
                  : <TrendingDown className={cn('size-3', t.concern ? 'text-alert-text' : 'text-muted-foreground')} />}
              </div>
              <div className="mt-0.5 text-lg font-semibold leading-none text-foreground">
                {t.series[t.series.length - 1].toLocaleString()}
                <span className="text-[11px] font-normal text-muted-foreground"> {t.unit}</span>
              </div>
              <div className="mt-2"><Sparkline values={t.series} concern={t.concern} /></div>
            </div>
          ))}
        </div>

        {/* Patterns — featured */}
        <div className="rounded-xl border border-border bg-ornament-plum-ultra-subtle p-4">
          <div className="mb-2.5 flex items-center gap-2">
            <AISparkle size="sm" />
            <h3 className="text-sm font-semibold text-foreground">Patterns SARA has connected</h3>
          </div>
          <ul className="space-y-2.5">
            {PATTERNS.map((p) => (
              <li key={p} className="flex gap-2 text-[13px] leading-snug text-foreground">
                <Sparkles className="mt-0.5 size-3.5 shrink-0 text-ornament-plum" />
                {p}
              </li>
            ))}
          </ul>
        </div>

        {/* Labs */}
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent labs & vitals</h3>
          <div className="space-y-2">
            {LABS.map((l) => (
              <div key={l.name} className="flex items-center justify-between gap-2 rounded-lg border border-border-subtle bg-card px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-foreground truncate">{l.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{l.source} · {l.date}</div>
                </div>
                <span className={cn(
                  'shrink-0 rounded-md px-2 py-0.5 text-xs font-medium',
                  l.flag === 'borderline' ? 'bg-warning-light text-warning-text' : 'bg-success-light text-success-text',
                )}>{l.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function Sparkline({ values, concern }: { values: number[]; concern: boolean }) {
  const w = 100, h = 22, min = Math.min(...values), max = Math.max(...values)
  const range = max - min || 1
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ')
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block">
      <polyline points={pts} fill="none" stroke={concern ? 'var(--alert)' : 'var(--success)'} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}
